import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { t } from '../i18n/nl.js';
import { useActionsCount } from '../contexts/ActionsCountContext';
import { fetchOpenActiesCount } from '../utils/actiesUtils';
import { getActiveClientMemberships, primaryMembershipCredits } from '../utils/membershipUtils';

const COACH_NAV = [
  { to: '/coach/dashboard',     label: t('nav.dashboard'),        icon: '📊' },
  { to: '/coach/rooster',       label: t('nav.rooster'),          icon: '📅' },
  { to: '/coach/clients',       label: t('nav.clients'),          icon: '👥' },
  { to: '/coach/prospects',     label: t('nav.prospects'),        icon: '🌱' },
  { to: '/coach/aanvragen',     label: t('nav.ptAanvragen'),      icon: '📩', badge: 'ptAanvragen' },
  { to: '/coach/acties',        label: t('nav.acties'),           icon: '⚡' },
  { to: '/coach/facturen',      label: t('nav.facturering'),      icon: '🧾' },
  { to: '/coach/opzeggingen',   label: t('nav.opzeggingen'),      icon: '📋' },
  { to: '/coach/recepten',      label: t('nav.recepten'),         icon: '🥗' },
  { to: '/coach/oefeningen',    label: t('nav.oefeningen'),       icon: '🏋️' },
  { to: '/coach/push',          label: t('nav.pushNotificaties'), icon: '🔔' },
  { to: '/beheer/abonnementen', label: t('nav.abonnementen'),     icon: '🏷️', adminOnly: true, sectionStart: true },
  { to: '/beheer/locaties',     label: t('nav.locaties'),         icon: '📍', adminOnly: true },
  { to: '/beheer/communicatie/email', label: t('nav.communicatie'), icon: '✉️', adminOnly: true },
  { to: '/beheer/organisatie/bedrijf', label: t('nav.organisatie'), icon: '🏢', adminOnly: true },
  { to: '/admin/gebruikers',    label: t('nav.gebruikersBeheer'), icon: '🔑', adminOnly: true },
  { to: '/admin/bugrapporten',  label: t('nav.bugrapporten'),     icon: '🐞', adminOnly: true },
  { to: '/admin/cron-dashboard',label: t('nav.cronDashboard'),    icon: '🕐', adminOnly: true },
  { to: '/mijn-profiel',        label: t('nav.mijnCoachprofiel'), icon: '👤', sectionStart: true },
];

const CLIENT_NAV = [
  { to: '/client/rooster',      label: t('nav.rooster'),        icon: '📅' },
  { to: '/client/voeding',      label: t('nav.voeding'),        icon: '🥗' },
  { to: '/client/lessen',       label: t('nav.mijnLessen'),     icon: '🏃' },
  { to: '/client/afspraken',    label: t('nav.mijnAfspraken'),  icon: '📋' },
  { to: '/client/trainingslogboek', label: t('nav.mijnTrainingen'), icon: '🏋️' },
  { to: '/client/facturen',     label: t('nav.mijnFacturen'),   icon: '🧾' },
  { to: '/client/credits',      label: t('nav.mijnCredits'),    icon: '💳' },
  { to: '/mijn-abonnement',     label: t('nav.mijnAbonnement'), icon: '📋' },
  { to: '/client/buddies',      label: t('nav.mijnBuddies'),    icon: '🤝' },
  { to: '/client/instellingen', label: t('nav.mijnGegevens'),   icon: '👤' },
];

const ROLE_LABELS = {
  superadmin:  t('nav.roles.superadmin'),
  admin_coach: t('nav.roles.admin_coach'),
  coach:       t('nav.roles.coach'),
  client:      t('nav.roles.client'),
};

export default function Layout() {
  const { user, role, signOut, isClientRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { count: openActiesCount, setCount: setOpenActiesCount } = useActionsCount();
  const [clientCredits, setClientCredits] = useState(null);
  const [clientKlantnummer, setClientKlantnummer] = useState(null);
  const [aanmaningBanner, setAanmaningBanner] = useState(null); // { due_date }
  const [pendingAgreementToken, setPendingAgreementToken] = useState(null); // token string
  const [ptAanvraagCount, setPtAanvraagCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!user?.id || isClientRole) return;
    fetchOpenActiesCount(user.id, role).then(count => setOpenActiesCount(count));
  }, [user?.id, role, isClientRole, location.pathname, setOpenActiesCount]);

  useEffect(() => {
    if (!user?.id || isClientRole) return;
    supabase
      .from('appointment_requests')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', user.id)
      .in('status', ['pending', 'rescheduled'])
      .then(({ count }) => setPtAanvraagCount(count ?? 0));
  }, [user?.id, isClientRole, location.pathname]);

  useEffect(() => {
    if (!isClientRole || !user?.id) return;
    getActiveClientMemberships(user.id)
      .then(memberships => {
        setClientCredits(memberships.length
          ? memberships.reduce((sum, m) => sum + primaryMembershipCredits(m), 0)
          : null);
      })
      .catch(() => setClientCredits(null));

    supabase
      .from('profiles')
      .select('customer_number')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => setClientKlantnummer(data?.customer_number ?? null));

    // Check openstaande aanmaningen
    supabase
      .from('invoices')
      .select('id, invoice_reminders(new_due_date, type)')
      .eq('client_id', user.id)
      .in('status', ['final_notice', 'collections'])
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) { setAanmaningBanner(null); return; }
        const reminders = data.invoice_reminders || [];
        const latest = reminders
          .filter(r => r.new_due_date)
          .sort((a, b) => new Date(b.new_due_date) - new Date(a.new_due_date))[0];
        setAanmaningBanner({ due_date: latest?.new_due_date || null });
      });

    // UITBREIDING 11: Check openstaand agreement_token
    supabase
      .from('agreement_tokens')
      .select('token')
      .eq('client_id', user.id)
      .is('used_at', null)
      .is('rejected_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setPendingAgreementToken(data?.token || null));
  }, [isClientRole, user?.id, location.pathname]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const roleLabel = ROLE_LABELS[role] ?? role;
  const isAdminRole = ['superadmin', 'admin_coach'].includes(role);
  const navItems = isClientRole
    ? CLIENT_NAV
    : COACH_NAV.filter(item => !item.adminOnly || isAdminRole);

  const portalLabel = isClientRole ? t('nav.clientPortal') : t('nav.coachPortal');

  const accountMenu = (extraClass = '') => (
    <details className={`portal-account-menu ${extraClass}`}>
      <summary aria-label={t('nav.accountMenu')}>
        <span className="portal-account-avatar" aria-hidden="true">
          {(user?.email?.[0] || '?').toUpperCase()}
        </span>
        <span className="portal-account-summary-copy">
          <strong>{roleLabel}</strong>
          <small>{user?.email}</small>
        </span>
        <span className="portal-account-chevron" aria-hidden="true">⌄</span>
      </summary>
      <div className="portal-account-popover">
        <div className="portal-account-mobile-copy">
          <strong>{roleLabel}</strong>
          <span>{user?.email}</span>
        </div>
        {isClientRole && clientKlantnummer && (
          <div className="portal-account-meta">{t('nav.klantnummer')} {clientKlantnummer}</div>
        )}
        {isClientRole && clientCredits !== null && (
          <div className="portal-account-meta">{clientCredits} {t('nav.creditsResterend')}</div>
        )}
        <button onClick={handleSignOut} className="portal-account-logout">
          {t('nav.uitloggen')}
        </button>
      </div>
    </details>
  );

  return (
    <div className="app-shell">
      {/* Mobiele topbar met hamburger-knop */}
      <div className="mobile-topbar">
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(o => !o)}
          aria-label={t('nav.menuOpenen')}
        >
          ☰
        </button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>
          {t('app.name')}
        </div>
        {accountMenu('portal-account-menu-mobile')}
      </div>

      {/* Backdrop achter uitgeklapte sidebar (mobiel) */}
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`app-sidebar${sidebarOpen ? ' is-open' : ''}`}>
        {/* Logo */}
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', letterSpacing: '-0.3px' }}>
            {t('app.name')}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
            {portalLabel}
          </div>
        </div>

        {/* Nav */}
        <nav className="app-sidebar-nav">
          {navItems.map(item => (
            <div key={item.to}>
            {item.sectionStart && <div className="sidebar-section-label">{item.adminOnly ? t('nav.beheer') : t('nav.persoonlijk')}</div>}
            <NavLink
              to={item.to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 'var(--radius)',
                color: isActive ? '#fff' : 'var(--color-sidebar-text)',
                background: isActive ? 'var(--color-sidebar-active)' : 'transparent',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
                marginBottom: 2,
                transition: 'background 0.15s',
              })}
              onMouseEnter={e => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.background = 'var(--color-sidebar-hover)';
                }
              }}
              onMouseLeave={e => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.to === '/coach/acties' && openActiesCount > 0 && (
                <span style={{
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 99,
                  minWidth: 18,
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  {openActiesCount}
                </span>
              )}
              {item.badge === 'ptAanvragen' && ptAanvraagCount > 0 && (
                <span style={{
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 99,
                  minWidth: 18,
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  {ptAanvraagCount}
                </span>
              )}
            </NavLink>
            </div>
          ))}
        </nav>

      </aside>

      {/* Main content */}
      <main className="app-main">
        <div className="portal-accountbar">
          {accountMenu()}
        </div>
        {/* Aanmaningsbanner voor cliënten */}
        {isClientRole && aanmaningBanner && (
          <div style={{
            background: '#dc2626',
            color: '#fff',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              ⚠ {t('banner.openstaandeAanmaning')}
              {aanmaningBanner.due_date && (
                <> Betaal voor <strong>{aanmaningBanner.due_date}</strong> om incassokosten te voorkomen.</>
              )}
            </span>
            <Link
              to="/client/facturen"
              style={{ color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'underline', flexShrink: 0 }}
            >
              {t('banner.bekijkFacturen')}
            </Link>
          </div>
        )}

        {/* UITBREIDING 11: Overeenkomstbanner */}
        {isClientRole && pendingAgreementToken && (
          <div style={{
            background: '#1d4ed8',
            color: '#fff',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              📋 {t('banner.openstaandeOvereenkomst')}
            </span>
            <Link
              to={`/overeenkomst/${pendingAgreementToken}`}
              style={{ color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'underline', flexShrink: 0 }}
            >
              {t('banner.bekijkOvereenkomst')} →
            </Link>
          </div>
        )}
        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
